import { useState, useCallback } from 'react';
import { validateField, validateForm } from '../utils/validation';

export const useValidation = (validationRules, initialData = {}) => {
  const [formData, setFormData] = useState(initialData);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});

  const handleFieldChange = useCallback((fieldName, value) => {
    setFormData(prev => ({ ...prev, [fieldName]: value }));
    
    // Validate field if it has been touched
    if (touched[fieldName]) {
      const error = validateField(value, validationRules[fieldName]);
      setErrors(prev => ({ ...prev, [fieldName]: error }));
    }
  }, [validationRules, touched]);

  const handleFieldBlur = useCallback((fieldName) => {
    setTouched(prev => ({ ...prev, [fieldName]: true }));
    
    const error = validateField(formData[fieldName], validationRules[fieldName]);
    setErrors(prev => ({ ...prev, [fieldName]: error }));
  }, [formData, validationRules]);

  const validateAllFields = useCallback(() => {
    const validation = validateForm(formData, validationRules);
    setErrors(validation.errors);
    
    // Mark all fields as touched
    const allTouched = {};
    Object.keys(validationRules).forEach(key => {
      allTouched[key] = true;
    });
    setTouched(allTouched);
    
    return validation;
  }, [formData, validationRules]);

  const resetForm = useCallback((newData = {}) => {
    setFormData(newData);
    setErrors({});
    setTouched({});
  }, []);

  const getFieldProps = useCallback((fieldName) => {
    return {
      value: formData[fieldName] || '',
      onChange: (e) => handleFieldChange(fieldName, e.target.value),
      onBlur: () => handleFieldBlur(fieldName),
      error: !!errors[fieldName],
      helperText: errors[fieldName]
    };
  }, [formData, errors, handleFieldChange, handleFieldBlur]);

  return {
    formData,
    errors,
    touched,
    handleFieldChange,
    handleFieldBlur,
    validateAllFields,
    resetForm,
    setFormData,
    getFieldProps,
    isValid: Object.keys(errors).length === 0,
    validateFormData: validateAllFields,
    resetValidation: resetForm
  };
};